import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';

// Components
import { Layout, RichTextContent } from 'components/common';
import { SideNav } from 'components/common';

// Services
import { getContentfulEditorialPage, getContentfulEditorialPages, getContentfulSettings } from 'services/contentful';
import { CfSettings, CfEditorialPage } from 'interfaces/Contentful';
import {NextSeo} from "next-seo";

interface Props {
  settings: CfSettings;
  page: CfEditorialPage;
}

export const getStaticProps: GetStaticProps<Props> = async ({ params, locale }) => {
  const slugs = params?.pages;
  if (!slugs) return { notFound: true };
  const slug = Array.isArray(slugs) ? slugs.join('/') : slugs;

  const settings = await getContentfulSettings(locale);
  const page = await getContentfulEditorialPage(slug, locale);

  if (!page.data) {
    return {
      notFound: true,
    };
  }

  return {
    props: { settings: settings.data, page: page.data },
    revalidate: 60, // Every minute
  };
};

interface Path {
  params: {
    pages: string[] | string;
  };
  locale: string;
}

export const getStaticPaths: GetStaticPaths = async (context) => {
  const res = await getContentfulEditorialPages();

  // Create paths for each page and locale there is
  const paths = res.data.reduce<Path[]>((paths, page) => {
    // Loop through every locale that is available by next.config.js
    const newPaths = context.locales?.map((locale) => ({
      params: { pages: page.slug.includes('/') ? page.slug.split('/') : [page.slug] },
      locale,
    }));

    if (!newPaths) {
      return paths;
    }

    return paths.concat(newPaths);
  }, []);

  return {
    paths,
    // Fallback shouldn't be enabled here or otherwise this route
    // will catch every page, even 404s, and we don't want that
    fallback: false,
  };
};

const StandardPage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({ page }) => {
  const pageMenuLinks = page.menu?.links?.map((l) => ({
    href: l.href,
    title: l.title,
  }));

  return (
      <>
        <NextSeo title={page.title} />
        <div className='padding-top-xl margin-bottom-xxl'>
          <article className='container max-width-adaptive-lg'>
            <div className='grid gap-lg justify-between'>
              <div className='col-12 col-6@md col-7@lg order-2 order-1@md'>
                <div className='text-component'>
                  <h1 className='font-bold'>{page.title}</h1>
                  <RichTextContent content={page.content} />
                </div>
              </div>
              <aside className='col-12 col-4@md col-3@lg order-1 order-2@md'>
                {page.menu && pageMenuLinks && (
                  <SideNav currentPage={page.title} links={[{ label: page.menu.label, links: pageMenuLinks }]} />
                )}
              </aside>
            </div>
          </article>
        </div>
      </>
  );
};

// @ts-ignore custom implementation that NextPage doesn't cover
StandardPage.Layout = Layout;

export default StandardPage;
